import { z } from 'zod';
export const solanaAddressSchema = z.string().regex(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/, 'Expected a valid Solana base58 address');
/** Use for Solana blockhashes — base58, same character set as addresses. */
export const solanaBlockhashSchema = z.string().regex(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/, 'Expected a valid Solana base58 blockhash');
export const solanaCommitmentSchema = z.enum(['processed', 'confirmed', 'finalized']);
export const getSolanaBalanceParamsSchema = z.object({
    address: solanaAddressSchema,
}).strict();
export const getSolanaAccountInfoParamsSchema = z.object({
    address: solanaAddressSchema,
    encoding: z.enum(['base64', 'jsonParsed']).optional(),
}).strict();
export const getSolanaTokenAccountsParamsSchema = z.object({
    owner: solanaAddressSchema,
    mint: solanaAddressSchema.optional(),
    programId: solanaAddressSchema.optional(),
}).strict();
export const getSolanaTransactionParamsSchema = z.object({
    signature: z.string().min(1),
}).strict();
export const getSolanaSignaturesParamsSchema = z.object({
    address: solanaAddressSchema,
    limit: z.number().int().positive().optional(),
    before: z.string().min(1).optional(),
    until: z.string().min(1).optional(),
    commitment: solanaCommitmentSchema.optional(),
}).strict();
export const getSolanaTokenSupplyParamsSchema = z.object({
    mint: solanaAddressSchema,
}).strict();
export const solanaAccountInfoSchema = z.object({
    data: z.union([
        z.tuple([z.string(), z.string()]),
        z.object({ program: z.string().min(1), parsed: z.unknown() }).strict(),
    ]),
    executable: z.boolean(),
    lamports: z.number().int().nonnegative(),
    owner: solanaAddressSchema,
    rentEpoch: z.number().int().nonnegative(),
    space: z.number().int().nonnegative(),
}).strict();
export const solanaBalanceDataSchema = z.object({
    value: z.number().int().nonnegative(),
}).strict();
export const solanaTokenAmountSchema = z.object({
    amount: z.string().min(1),
    decimals: z.number().int().nonnegative(),
    uiAmount: z.union([z.number(), z.null()]),
    uiAmountString: z.string().min(1),
}).strict();
export const solanaTokenAccountSchema = z.object({
    pubkey: solanaAddressSchema,
    account: z.object({
        data: z.object({
            parsed: z.object({
                info: z.object({
                    mint: solanaAddressSchema,
                    owner: solanaAddressSchema,
                    tokenAmount: solanaTokenAmountSchema,
                }).strict(),
                type: z.string().min(1),
            }).strict(),
            program: z.string().min(1),
            space: z.number().int().nonnegative(),
        }).strict(),
        executable: z.boolean(),
        lamports: z.number().int().nonnegative(),
        owner: solanaAddressSchema,
        rentEpoch: z.number().int().nonnegative(),
    }).strict(),
}).strict();
export const solanaTokenAccountsDataSchema = z.object({
    value: z.array(solanaTokenAccountSchema),
}).strict();
export const solanaSignatureStatusSchema = z.object({
    slot: z.number().int().nonnegative(),
    confirmations: z.union([z.number().int().nonnegative(), z.null()]),
    err: z.unknown(),
    confirmationStatus: solanaCommitmentSchema,
}).strict();
export const solanaTransactionDataSchema = z.object({
    slot: z.number().int().nonnegative(),
    blockTime: z.union([z.number().int(), z.null()]),
    meta: z.union([
        z.object({
            err: z.unknown(),
            fee: z.number().int().nonnegative(),
            computeUnitsConsumed: z.number().int().nonnegative().optional(),
            logMessages: z.array(z.string()).optional(),
        }).strict(),
        z.null(),
    ]),
    transaction: z.unknown(),
}).strict();
export const solanaSignatureInfoSchema = z.object({
    signature: z.string().min(1),
    slot: z.number().int().nonnegative(),
    err: z.unknown(),
    memo: z.union([z.string(), z.null()]),
    blockTime: z.union([z.number().int(), z.null()]),
    confirmationStatus: z.string().min(1),
}).strict();
export const solanaLatestBlockhashDataSchema = z.object({
    blockhash: z.string().min(1),
    lastValidBlockHeight: z.number().int().nonnegative(),
}).strict();
export const solanaEpochInfoDataSchema = z.object({
    epoch: z.number().int().nonnegative(),
    slotIndex: z.number().int().nonnegative(),
    slotsInEpoch: z.number().int().nonnegative(),
    absoluteSlot: z.number().int().nonnegative(),
    blockHeight: z.number().int().nonnegative(),
}).strict();
export const solanaTokenSupplyDataSchema = solanaTokenAmountSchema;
